import { create } from 'zustand'

import { authStorage, normalizeBearerToken } from '@/lib/auth-storage'
import type { User } from '@/lib/permission'

interface AuthState {
  user: User | null
  accessToken: string | null
  // Absolute expiry (epoch ms) of the access token, or null when unknown.
  // Drives proactive refresh in `useTokenAutoRefresh`.
  expiresAt: number | null
  setAuth: (payload: {
    user: User
    accessToken: string
    refreshToken: string
    expiresInSec?: number
  }) => void
  setTokens: (accessToken: string) => void
  setUser: (user: User) => void
  logout: () => void
  clearAuth: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  accessToken: authStorage.getAccess(),
  expiresAt: authStorage.getExpiresAt(),
  setAuth: ({ user, accessToken, refreshToken, expiresInSec }) => {
    authStorage.set(accessToken, refreshToken, expiresInSec)
    set({
      user,
      accessToken: normalizeBearerToken(accessToken),
      expiresAt: authStorage.getExpiresAt(),
    })
  },
  // The new access token has already been persisted (with its expiry) by
  // `refreshAccessToken` before this runs, so we read the freshly stored expiry.
  setTokens: (accessToken) =>
    set({
      accessToken: normalizeBearerToken(accessToken),
      expiresAt: authStorage.getExpiresAt(),
    }),
  setUser: (user) => set({ user }),
  logout: () => {
    authStorage.clear()
    set({ user: null, accessToken: null, expiresAt: null })
  },
  clearAuth: () => useAuthStore.getState().logout(),
}))
